import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchCollection, fetchEntryBySlug } from '../lib/cms';
import Lightbox from '../components/Lightbox';

function parseReviewDate(dateStr) {
  if (!dateStr) return 0;
  const parts = String(dateStr).trim().split('-');
  if (parts.length === 3) {
    // Format: DD-MM-YYYY
    if (parts[2].length === 4) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day).getTime();
    }
    // Format: YYYY-MM-DD
    if (parts[0].length === 4) {
      return new Date(dateStr).getTime();
    }
  }
  const parsed = new Date(dateStr).getTime();
  return isNaN(parsed) ? 0 : parsed;
}

export default function Anmeldelser() {
  const [pageData, setPageData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lightbox tilstand
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Formular tilstand
  const [newReview, setNewReview] = useState({
    name: '',
    title: '',
    content: '',
    website_url: '' // Honeypot
  });
  const [formStatus, setFormStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [formError, setFormStatusError] = useState('');

  // Karrusel og visningstilstand
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [expandedReviews, setExpandedReviews] = useState({});

  useEffect(() => {
    async function loadReviews() {
      try {
        const [page, allReviews] = await Promise.all([
          fetchEntryBySlug('da', 'anmeldelser'),
          fetchCollection('reviews')
        ]);
        if (page) setPageData(page);
        const sorted = [...allReviews].sort((a, b) => parseReviewDate(b.date) - parseReviewDate(a.date));
        setReviews(sorted);
      } catch (err) {
        console.error('Error loading reviews:', err);
      } finally {
        setLoading(false);
      }
    }
    loadReviews();
  }, []);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 1024) {
        setVisibleCount(3);
      } else if (window.innerWidth >= 768) {
        setVisibleCount(2);
      } else {
        setVisibleCount(1);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, reviews.length - visibleCount);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const handleOpenLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const handlePrevImage = () => {
    setLightboxIndex((prev) => (prev === 0 ? lightboxImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setLightboxIndex((prev) => (prev === lightboxImages.length - 1 ? 0 : prev + 1));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewReview(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormStatusError('');

    if (!newReview.name || !newReview.content) {
      setFormStatus('error');
      setFormStatusError('Venligst udfyld både navn og anmeldelse.');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newReview.name,
          email: 'no-reply@skt-byg.dk', // Standard email til anmeldelser
          message: `Overskrift: ${newReview.title}\n\nAnmeldelse:\n${newReview.content}`,
          website_url: newReview.website_url, // Honeypot
          subject: 'Ny kundeanmeldelse til gennemsyn'
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'ok') {
        setFormStatus('success');
        setNewReview({ name: '', title: '', content: '', website_url: '' });
      } else {
        setFormStatus('error');
        setFormStatusError(data.message || 'Der skete en fejl under indsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      setFormStatus('error');
      setFormStatusError('Der opstod en netværksfejl. Prøv venligst igen.');
    }
  };

  const metaTitle = pageData?.meta_title?.da || pageData?.meta_title || "Kundeanmeldelser & Erfaringer | Hvad siger kunderne om SKT Byg?";
  const metaDesc = pageData?.meta_description?.da || pageData?.meta_description || "Læs ægte anmeldelser og anbefalinger fra kunder, der har fået udført tømrer- og murerarbejde af SKT Byg i Galten og omegn. Se erfaringerne her.";

  const pageTitle = pageData?.page_title?.da || pageData?.page_title || "Anmeldelser";
  const pageSubtitle = pageData?.page_subtitle?.da || pageData?.page_subtitle || "Vi har gennem tiden hjulpet mange tilfredse kunder. Vi vil kun have glade og tilfredse kunder. Her på siden kan du læse vores kunders anmeldelser om vores arbejde.";

  return (
    <div className="min-h-screen bg-brand-light py-16">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/kirsten-1.jpg" />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Sidetitel */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold text-brand-dark mb-4">
            {pageTitle}
          </h1>
          <p className="text-lg text-brand-dark/70 leading-relaxed">
            {pageSubtitle}
          </p>
        </div>

        {/* Anmeldelsesliste som karrusel */}
        <div className="relative mb-20 px-2 md:px-12">
          {/* Arrows */}
          {reviews.length > visibleCount && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white border border-brand-sand shadow-md flex items-center justify-center text-brand-dark hover:bg-brand-accent hover:text-white hover:border-brand-accent transition-all duration-300 cursor-pointer"
                aria-label="Forrige anmeldelse"
              >
                <i className="fa-solid fa-chevron-left text-xs md:text-sm"></i>
              </button>
              <button
                onClick={handleNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white border border-brand-sand shadow-md flex items-center justify-center text-brand-dark hover:bg-brand-accent hover:text-white hover:border-brand-accent transition-all duration-300 cursor-pointer"
                aria-label="Næste anmeldelse"
              >
                <i className="fa-solid fa-chevron-right text-xs md:text-sm"></i>
              </button>
            </>
          )}

          {/* Carousel Container */}
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
              }}
            >
              {reviews.map((review) => {
                const isExpanded = !!expandedReviews[review.id];
                const rawText = review.content ? review.content.replace(/<[^>]*>/g, '') : '';
                const isLong = rawText.length > 150;
                const displayContent = isLong && !isExpanded 
                  ? rawText.slice(0, 150) + '...' 
                  : review.content;

                return (
                  <div
                    key={review.id}
                    className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-4 flex flex-col"
                  >
                    <div className="bg-white p-8 border border-brand-sand shadow-sm flex flex-col justify-between h-full min-h-[340px]">
                      <div>
                        <div className="flex items-center justify-between gap-4 pb-4 border-b border-brand-sand mb-4">
                          <div>
                            <h3 className="text-sm font-bold text-brand-dark flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span>{review.author}</span>
                              <span className="text-xs font-semibold text-brand-dark/40">·</span>
                              <span className="text-xs font-semibold text-brand-dark/50">{review.date}</span>
                            </h3>
                            {review.title && (
                              <h4 className="text-xs font-bold text-brand-accent mt-1">
                                {review.title}
                              </h4>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-brand-accent shrink-0">
                            {[...Array(5)].map((_, i) => (
                              <i key={i} className="fa-solid fa-star text-[10px]"></i>
                            ))}
                          </div>
                        </div>

                        <div className="text-sm text-brand-dark/80 leading-relaxed mb-4">
                          {isLong && !isExpanded ? (
                            <p>{displayContent}</p>
                          ) : (
                            <div dangerouslySetInnerHTML={{ __html: displayContent }} />
                          )}
                        </div>

                        {isLong && (
                          <button
                            onClick={() => setExpandedReviews(prev => ({ ...prev, [review.id]: !prev[review.id] }))}
                            className="text-xs font-bold text-brand-dark hover:text-brand-accent transition-colors duration-300 mb-4 flex items-center gap-1 cursor-pointer"
                          >
                            {isExpanded ? 'Vis mindre' : 'Læs mere'}
                            <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} text-[10px]`}></i>
                          </button>
                        )}
                      </div>

                      {/* Billeder tilknyttet anmeldelsen */}
                      {review.images && review.images.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 pt-4 border-t border-brand-sand mt-auto">
                          {review.images.map((img, imgIdx) => (
                            <div
                              key={imgIdx}
                              onClick={() => handleOpenLightbox(review.images, imgIdx)}
                              className="relative h-12 rounded-lg overflow-hidden border border-brand-sand cursor-pointer group"
                            >
                              <img
                                src={img}
                                alt={`Anmeldelsesbillede ${imgIdx + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-all duration-300">
                                <i className="fa-solid fa-magnifying-glass-plus text-white opacity-0 group-hover:opacity-100 text-[10px] transition-opacity duration-300"></i>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dots navigation */}
          {reviews.length > visibleCount && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                    currentIndex === idx ? 'bg-brand-accent w-6' : 'bg-brand-sand hover:bg-brand-dark/40'
                  }`}
                  aria-label={`Gå til dias ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Skriv en anmeldelse */}
        <div className="bg-white border border-brand-sand rounded-3xl p-8 md:p-12">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-extrabold text-brand-dark mb-4">
              Har vi udført arbejde for dig?
            </h2>
            <p className="text-brand-dark/70">
              Vi er altid glade for at høre fra vores kunder. Skriv en anmeldelse i formularen nedenfor, så kommer den på siden efter gennemsyn.
            </p>
          </div>

          {formStatus === 'success' ? (
            <div className="bg-white border border-emerald-200 rounded-2xl p-8 text-center shadow-sm max-w-xl mx-auto">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 mb-4">
                <i className="fa-solid fa-check text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Tak for din anmeldelse!</h3>
              <p className="text-slate-600 text-sm">
                Din anmeldelse er modtaget og vil blive vist på siden efter gennemsyn.
              </p>
              <button
                onClick={() => setFormStatus('idle')}
                className="mt-6 px-6 py-2 bg-brand-dark text-white rounded-lg font-medium hover:bg-brand-accent transition-colors duration-300 cursor-pointer"
              >
                Skriv en ny anmeldelse
              </button>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="bg-white p-6 md:p-8 rounded-2xl border border-brand-sand shadow-sm max-w-xl mx-auto space-y-6">
              {/* Honeypot */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url_review">Efterlad tomt</label>
                <input
                  type="text"
                  id="website_url_review"
                  name="website_url"
                  value={newReview.website_url}
                  onChange={handleFormChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div>
                <label htmlFor="review_name" className="block text-sm font-semibold text-brand-dark mb-2">
                  Navn <span className="text-brand-accent">*</span>
                </label>
                <input
                  type="text"
                  id="review_name"
                  name="name"
                  required
                  value={newReview.name}
                  onChange={handleFormChange}
                  placeholder="Dit navn"
                  className="w-full px-4 py-3 rounded-xl border border-brand-sand bg-brand-light text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent transition-all duration-300"
                />
              </div>

              <div>
                <label htmlFor="review_title" className="block text-sm font-semibold text-brand-dark mb-2">
                  Overskrift
                </label>
                <input
                  type="text"
                  id="review_title"
                  name="title"
                  value={newReview.title}
                  onChange={handleFormChange}
                  placeholder="F.eks. Flot arbejde eller Super service"
                  className="w-full px-4 py-3 rounded-xl border border-brand-sand bg-brand-light text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent transition-all duration-300"
                />
              </div>

              <div>
                <label htmlFor="review_content" className="block text-sm font-semibold text-brand-dark mb-2">
                  Anmeldelse <span className="text-brand-accent">*</span>
                </label>
                <textarea
                  id="review_content"
                  name="content"
                  required
                  rows="4"
                  value={newReview.content}
                  onChange={handleFormChange}
                  placeholder="Skriv din anmeldelse her..."
                  className="w-full px-4 py-3 rounded-xl border border-brand-sand bg-brand-light text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent transition-all duration-300 resize-none"
                ></textarea>
              </div>

              {formStatus === 'error' && (
                <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-sm flex items-center gap-3">
                  <i className="fa-solid fa-circle-exclamation text-base shrink-0"></i>
                  <span>{formError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3.5 bg-brand-dark text-white font-bold rounded-xl hover:bg-brand-accent transition-all duration-300 shadow-md flex items-center justify-center gap-2 cursor-pointer"
              >
                {formStatus === 'submitting' ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin"></i>
                    <span>Indsender...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-paper-plane"></i>
                    <span>Indsend anmeldelse</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {isLightboxOpen && (
        <Lightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setIsLightboxOpen(false)}
          onPrev={handlePrevImage}
          onNext={handleNextImage}
        />
      )}
    </div>
  );
}
